// Per-dialect synthesizer for PRIMARY KEY add/drop ops (v2 Slice B1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B1.
//
// Each engine emits the dialect-correct ADD/DROP CONSTRAINT clause:
//   - mssql:  `ADD [CONSTRAINT name] PRIMARY KEY [CLUSTERED|NONCLUSTERED] (cols)`
//             / `DROP CONSTRAINT name`. CLUSTERED/NONCLUSTERED preserved.
//   - pgsql:  `ADD CONSTRAINT name PRIMARY KEY (cols) [INCLUDE (cols)]`
//             / `DROP CONSTRAINT name [CASCADE]`. INCLUDE is 11+.
//   - mysql:  `ADD [CONSTRAINT name] PRIMARY KEY (cols)`
//             / `DROP PRIMARY KEY`. mysql's PK is always named `PRIMARY`
//             internally, so DROP PRIMARY KEY targets the implicit object;
//             a passed `constraintName` is ignored on drop.
//   - oracle: `ADD CONSTRAINT name PRIMARY KEY (cols) [USING INDEX TABLESPACE ts]
//             [DEFERRABLE INITIALLY {DEFERRED|IMMEDIATE}]`
//             / `DROP CONSTRAINT name [CASCADE]`.
//   - sqlite: not supported on ALTER → throws (DSE2-6). PK in CREATE TABLE
//             only; there is no automated recreate helper for this op (the
//             opt-in `sqliteRecreate` primitive only rewrites columns) — the
//             table must be rebuilt by hand.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { PrimaryKeyDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { type ExistingNameOrigin, nameExistingColumn } from './dialect.quoting';
import type { ColumnListOrigins } from './existingNameEvidence';

export interface PrimaryKeySynthOpts {
  /** The DSE6 STYLE flag — the host relation's own quoting, mirrored onto the
   *  column tokens. A style may ADD delimiters; it may never decide WHICH
   *  columns are named. See `columnNameOrigin`. */
  readonly quoteName: boolean;
  /** WHERE the spelling at each POSITION of `pk.columns` came from. A PRIMARY
   *  KEY names columns the catalog ALREADY HOLDS — exactly like a rename's old
   *  name, a drop's target and an ALTER COLUMN's subject — so every element
   *  sits on the same three-way contract (`dialect.quoting.ts`'s
   *  {@link ExistingNameOrigin}) and `quoteName` alone cannot answer it. Asked
   *  PER ELEMENT because one list may legitimately hold a source-delimited
   *  `"Id"` next to a bare `target_col`, and a single answer for the whole
   *  list corrupts one of them. Defaults to `'bare-reference'`, the contract a
   *  caller holding only a name is under. */
  readonly columnNameOrigin?: ColumnListOrigins;
  /** The same question for `pk.includeColumns`, asked SEPARATELY. INCLUDE is
   *  a second list on one edit, not a continuation of the first: an element is
   *  identified by its position in the list being emitted, so a shared lookup
   *  serves one list's answer to the other and the key names a different
   *  column. The server does not stop it — PostgreSQL accepts an INCLUDE that
   *  overlaps its own key — so the separation has to hold here. Defaults to
   *  `'bare-reference'` independently of `columnNameOrigin`. */
  readonly includeColumnNameOrigin?: ColumnListOrigins;
}

/** The default origin resolver: every element is a reference the caller wrote
 *  bare, which is what a free-text column list is until the source statement
 *  says otherwise. */
const BARE_REFERENCE = (): ExistingNameOrigin => 'bare-reference';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

/** Name each column the constraint lists. `styleDelimits` may only ADD
 *  delimiters; `nameExistingColumn` picks the TEXT that goes inside them per
 *  element, because adding delimiters to a bare reference suppresses the very
 *  fold that was resolving it, and folding a spelling the server already
 *  stores names a different column.
 *
 *  The origin is asked for by POSITION: two elements of one list may carry the
 *  same bytes (`Id` and `"Id"` decode alike) and only their positions tell
 *  them apart. */
function quoteCols(
  engine: SqlEngine,
  cols: readonly string[],
  styleDelimits: boolean,
  originAt: ColumnListOrigins,
): string {
  return cols.map((c, i) => {
    const { text, delimit } = nameExistingColumn(c, engine, originAt(i), styleDelimits);
    return maybeQuoteIdent(engine, text, delimit);
  }).join(', ');
}

function rejectField(engine: SqlEngine, field: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} PRIMARY KEY does not accept '${field}'`,
  );
}

function validatePk(engine: SqlEngine, pk: PrimaryKeyDef): void {
  if (pk.columns.length === 0) {
    throw new Error(`applyDdlEdit: PRIMARY KEY requires at least one column`);
  }
  if (engine !== 'mssql' && pk.clustered !== undefined) rejectField(engine, 'clustered');
  if (engine !== 'postgres' && pk.includeColumns !== undefined) {
    rejectField(engine, 'includeColumns');
  }
  if (engine !== 'oracle' && pk.tablespace !== undefined) rejectField(engine, 'tablespace');
  if (engine !== 'oracle' && engine !== 'postgres' && pk.deferrable !== undefined) {
    rejectField(engine, 'deferrable');
  }
}

export function synthesizeAddPrimaryKey(
  engine: SqlEngine,
  table: QualifiedName,
  pk: PrimaryKeyDef,
  opts: PrimaryKeySynthOpts,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … ADD CONSTRAINT `
        + `PRIMARY KEY. Define the primary key inline at CREATE TABLE time, or `
        + `rebuild the table manually (CREATE TABLE with the constraint, copy `
        + `the data across, DROP the old table, RENAME the new one) — the `
        + `opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  validatePk(engine, pk);
  const tbl = tableText(engine, table);
  const originOf = opts.columnNameOrigin ?? BARE_REFERENCE;
  const cols = quoteCols(engine, pk.columns, opts.quoteName, originOf);
  const constraintPrefix = pk.name ? `CONSTRAINT ${pk.name} ` : '';

  if (engine === 'mssql') {
    const cluster = pk.clustered === 'nonclustered'
      ? ' NONCLUSTERED'
      : pk.clustered === 'clustered' ? ' CLUSTERED' : '';
    return `ALTER TABLE ${tbl} ADD ${constraintPrefix}PRIMARY KEY${cluster} (${cols})`;
  }

  if (engine === 'postgres') {
    let s = `ALTER TABLE ${tbl} ADD ${constraintPrefix}PRIMARY KEY (${cols})`;
    if (pk.includeColumns && pk.includeColumns.length > 0) {
      const includeOriginOf = opts.includeColumnNameOrigin ?? BARE_REFERENCE;
      s += ` INCLUDE (${quoteCols(engine, pk.includeColumns, opts.quoteName, includeOriginOf)})`;
    }
    if (pk.deferrable !== undefined) {
      s += ` DEFERRABLE INITIALLY ${pk.deferrable === 'deferred' ? 'DEFERRED' : 'IMMEDIATE'}`;
    }
    return s;
  }

  if (engine === 'mysql') {
    return `ALTER TABLE ${tbl} ADD ${constraintPrefix}PRIMARY KEY (${cols})`;
  }

  // oracle
  let s = `ALTER TABLE ${tbl} ADD ${constraintPrefix}PRIMARY KEY (${cols})`;
  if (pk.tablespace) s += ` USING INDEX TABLESPACE ${pk.tablespace}`;
  if (pk.deferrable !== undefined) {
    s += ` DEFERRABLE INITIALLY ${pk.deferrable === 'deferred' ? 'DEFERRED' : 'IMMEDIATE'}`;
  }
  return s;
}

export function synthesizeDropPrimaryKey(
  engine: SqlEngine,
  table: QualifiedName,
  constraintName: string | undefined,
  cascade: boolean | undefined,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … DROP PRIMARY KEY. `
        + `Rebuild the table manually (CREATE TABLE without the constraint, `
        + `copy the data across, DROP the old table, RENAME the new one) — `
        + `the opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  const tbl = tableText(engine, table);

  if (engine === 'mysql') {
    // mysql's PK is always named PRIMARY internally; DROP PRIMARY KEY
    // targets it directly. constraintName is accepted for API symmetry but
    // ignored — there is no `DROP CONSTRAINT primary` on mysql.
    return `ALTER TABLE ${tbl} DROP PRIMARY KEY`;
  }

  if (!constraintName) {
    throw new Error(
      `applyDdlEdit: ${engine} DROP PRIMARY KEY requires 'constraintName' — `
        + `${engine} drops PRIMARY KEY constraints by name (ALTER TABLE … `
        + `DROP CONSTRAINT <name>)`,
    );
  }
  const cas = cascade ? ' CASCADE' : '';
  return `ALTER TABLE ${tbl} DROP CONSTRAINT ${constraintName}${cas}`;
}
